import { useTheme } from '@core/lib/useTheme';

/**
 * Light ⇄ dark, in the header beside the cart.
 *
 * A BUTTON, NOT A THREE-WAY MENU, even though the underlying state has three
 * values. Nobody opens a menu to change the brightness of a page; they press
 * the thing and look at the result. "System" stays reachable as the state a
 * visitor starts in and never has to think about, and pressing the button is
 * taken as expressing a preference — which is exactly what it is.
 *
 * The icon shows the destination, not the current state. A sun on a dark page
 * means "press for light", which is the reading people expect; showing the
 * current state instead leaves them guessing whether it is a label or a
 * switch. The accessible name says it in words either way.
 */
export function ThemeToggle() {
  const { resolved, toggle } = useTheme();
  const goingTo = resolved === 'dark' ? 'light' : 'dark';

  return (
    <button
      type="button"
      onClick={toggle}
      // aria-pressed would be wrong: this is not a control that is on or off,
      // it is one that swaps between two equal states.
      aria-label={`Switch to ${goingTo} mode`}
      title={`Switch to ${goingTo} mode`}
      className="grid min-h-touch min-w-touch place-items-center rounded-control text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
    >
      {/* 24px grid, 2px stroke, round caps — the guide's icon rules. */}
      <svg
        viewBox="0 0 24 24"
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {goingTo === 'light' ? (
          <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </>
        ) : (
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
        )}
      </svg>
    </button>
  );
}
